import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faPlayCircle } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import type { Video } from '@/models';

interface RecentVideosCardProps {
  videos: Video[];
  className?: string;
}

export default function RecentVideosCard({ videos = [], className = '' }: RecentVideosCardProps) {
  // Show only the first 3 videos
  const recentVideos = videos.slice(0, 3);

  // Format date to German format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700 h-[320px] flex flex-col ${className}`}>
      <div className="flex items-center space-x-2 mb-4">
        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
          <FontAwesomeIcon icon={faVideo} className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Neueste Videos
        </h3>
      </div>

      {/* Videos Grid */}
      <div className="flex-1 flex flex-col sm:justify-center overflow-y-auto">
        {recentVideos.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Keine Videos verfügbar
            </p>
            <Link
              to="/videos"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors cursor-pointer"
            >
              <FontAwesomeIcon icon={faVideo} className="w-4 h-4" />
              <span>Zur Academy</span>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 h-auto sm:h-full">
            {recentVideos.map((video) => (
              <Link
                key={video.id}
                to={`/courses/${video.courseID}/modules/${video.moduleID}/videos/${video.id}`}
                className="group bg-gray-50 dark:bg-gray-700/50 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all hover:shadow-lg flex flex-col"
              >
                {/* Thumbnail */}
                <div className="relative w-full aspect-video bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden">
                  {video.thumbnail !== null ? (
                    <img
                      src={APIRoutes.getBlobContentUrl(video.thumbnail)}
                      alt={video.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faVideo}
                      className="w-8 h-8 text-gray-400 dark:text-gray-500"
                    />
                  )}
                  {/* Play overlay */}
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={faPlayCircle}
                      className="w-10 h-10 text-white"
                    />
                  </div>
                </div>

                {/* Video Info */}
                <div className="p-2 flex-1 flex flex-col justify-between">
                  <div className="flex-1 flex items-center justify-center">
                    <h4 className="text-xs font-semibold text-gray-900 dark:text-white line-clamp-2 text-center">
                      {video.title}
                    </h4>
                  </div>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">
                    {formatDate(video.publishTimestamp ?? video.created)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
